import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CommentModal } from "@/components/CommentModal";
import { PhotoViewerMobile } from "@/components/PhotoViewerMobile";
import { VideoViewerMobile } from "@/components/VideoViewerMobile";
import { useIsMobile } from "@/lib/useIsMobile";
import { mobileViewerKind } from "@/lib/postMedia";
import { usePostPatch, applyPostPatch } from "@/lib/postSync";
import { authPath, stashReturnTo } from "@/lib/guestGate";
import { useSeo, excerpt } from "@/lib/seo";

// THE post screen: every /post/:id link (notification, share, search, replay, deep link,
// photo tap) opens the same « Publication de … » CommentModal the feed and Reels use.
// ?photo=N : PC → Facebook-style photo theater (photo left, post + comments right) ;
// mobile → fullscreen PhotoViewerMobile (author, text, reactions, Reels-style comments sheet).
// ?focus=comments&comment=… scrolls to a comment. Closing returns to the previous screen (home on cold start).
// Signed-out visitor (shared link): same screens in read-only mode with a sign-in invitation;
// closing (✕ / back) leads to the sign-in screen and the post is reopened after signing in.
export default function PostComments() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { t, user } = useApp();
  const isMobile = useIsMobile();
  const [post, setPost] = useState(null);
  usePostPatch(post?.id, (patch) => setPost((p) => (p ? applyPostPatch(p, patch) : p)));
  const [err, setErr] = useState(false);
  const photoParam = params.get("photo");
  const initialPhoto = photoParam !== null && photoParam !== "" ? Math.max(0, parseInt(photoParam, 10) || 0) : null;

  useEffect(() => {
    api.get(`/posts/${id}`).then(({ data }) => {
      setPost(data.post);
      // Count one replay view per browser (deliberate open of a rediffusion).
      if (data.post?.is_replay) {
        const key = `replay_viewed_${id}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, "1");
          api.post(`/posts/${id}/replay-view`).catch(() => {});
        }
      }
    }).catch((e) => {
      const d = e?.response?.data?.detail;
      // Lot 649 — audience restreinte (Abonnés / Suivis / Moi…) : même écran « réservée » que le profil privé.
      setErr(d?.code === "private_profile" ? { private: true, authorId: d.author_id } : d?.code === "post_audience" ? { private: true, audience: d.audience, authorId: d.author_id } : true);
    });
  }, [id]);

  const toSignIn = () => { stashReturnTo(); nav(authPath("login"), { replace: true }); };
  // SEO : titre « Auteur sur Yalola : extrait », description = texte du post, canonique /post/{id}, image de partage.
  const seoText = post ? excerpt(post.verse ? (post.verse.note_fr || post.content) : post.content, 150) : "";
  useSeo(post ? {
    title: `${post.author?.name || "Membre Yalola"} sur Yalola${seoText ? ` : ${excerpt(seoText, 70)}` : ""}`,
    description: seoText || `Publication de ${post.author?.name || "un membre"} sur Yalola, le réseau chrétien qui rassemble.`,
    canonical: `/post/${id}`, type: "article", image: `${process.env.REACT_APP_PUBLIC_URL || "https://yalola.app"}/api/e/post/${id}/share.jpg`,
  } : (err ? { title: t("theater.not_found", "Publication introuvable"), robots: "noindex,follow", canonical: `/post/${id}` } : null), [post?.id, err, id]);
  // Garde-fou : un seul retour arrière même si la visionneuse déclenche onClose deux fois (double tap, propagation).
  const closing = useRef(false);
  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setTimeout(() => { closing.current = false; }, 800);
    if (!user) { toSignIn(); return; }
    if (window.history.length > 1) nav(-1); else nav("/home", { replace: true });
  };
  const onPhotoClose = () => {
    if (photoParam === null) return;
    const next = new URLSearchParams(params);
    next.delete("photo");
    setParams(next, { replace: true });
  };
  const kind = mobileViewerKind(post);
  const focusComment = params.get("comment") || null;
  const wantComments = !!focusComment || params.get("focus") === "comments";
  // Notification « X a réagi à votre publication » (?focus=reactions) : la barre de réactions défile + pulse (PC et téléphone).
  const focusReactions = params.get("focus") === "reactions";
  // Téléphone (façon Instagram) : dès qu'il y a un média, la publication s'ouvre dans la visionneuse
  // plein écran ; « Commenter » / notification → feuille de commentaires déjà ouverte.
  // Carte texte sur fond coloré → même visionneuse que les photos ; vidéo par lien (YouTube / Facebook) →
  // visionneuse vidéo (lecteur intégré, son). Fiche claire « Publication de … » seulement pour les posts
  // texte simple / audio / lien article (et sur PC).
  const mobilePhoto = isMobile && (kind === "photo" || kind === "text");
  const mobileVideo = isMobile && (kind === "video" || kind === "embed");

  return (
    <div className="min-h-screen grid place-items-center" style={{ background: "var(--yalola-bg)" }} data-testid="post-comments-host">
      {!post && !err && <Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} data-testid="post-comments-loading" />}
      {err && (
        <div className="yalola-card p-8 text-center space-y-3" data-testid="post-comments-error">
          {err?.private ? (
            <>
              <div className="mx-auto h-12 w-12 rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Lock size={22} /></div>
              <div className="font-semibold" data-testid="post-private-title">{err.audience ? t("post.audience_title", "Publication réservée") : t("post.private_title", "Publication privée")}</div>
              <div className="text-sm max-w-sm" style={{ color: "var(--yalola-text-muted)" }}>{err.audience ? t("post.audience_hint", "L'auteur a limité cette publication à un cercle restreint (abonnés, suivis ou lui seul). Tu n'en fais pas partie pour l'instant.") : t("post.private_hint", "Cette publication appartient à un compte privé. Suis ce compte et attends son accord pour la voir.")}</div>
              {err.authorId && <div><button className="pill pill-primary h-10 px-5 text-sm font-semibold" onClick={() => nav(`/profile/${err.authorId}`)} data-testid="post-private-profile">{t("post.private_open_profile", "Voir le profil")}</button></div>}
            </>
          ) : (
            <div>{t("theater.not_found", "Publication introuvable")}</div>
          )}
          {!user && (
            <>
              <div className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="post-comments-guest-hint">{t("guest.view_login_needed", "Connecte-toi pour voir cette publication.")}</div>
              <button className="pill pill-primary h-10 px-5 text-sm font-semibold" onClick={toSignIn} data-testid="post-comments-login">{t("guest.login", "Se connecter")}</button>
            </>
          )}
          <div><button className="text-sm underline" style={{ color: "var(--yalola-primary)" }} onClick={close} data-testid="post-comments-back">{t("common.back", "Retour")}</button></div>
        </div>
      )}
      {post && mobilePhoto && (
        <PhotoViewerMobile post={post} index={initialPhoto ?? 0} onClose={close} initialComments={wantComments} focusCommentId={focusComment} focusReactions={focusReactions} />
      )}
      {post && mobileVideo && (
        <VideoViewerMobile post={post} onClose={close} initialComments={wantComments} focusCommentId={focusComment} focusReactions={focusReactions} />
      )}
      {post && !mobilePhoto && !mobileVideo && (
        <CommentModal
          post={post}
          open
          onOpenChange={(o) => { if (!o) close(); }}
          focusCommentId={focusComment}
          focusReactions={focusReactions}
          initialPhoto={initialPhoto}
          onPhotoClose={onPhotoClose}
        />
      )}
    </div>
  );
}
